/**
 * Modal stack composable
 *
 * Coordinates multiple simultaneously open Modal instances by maintaining
 * a shared reactive stack. Handles z-index layering, reference-counted
 * scroll lock, and single-point Escape key delegation so that only the
 * topmost modal responds to dismiss interactions.
 *
 * This composable is internal to Modal.vue — consumers never import it.
 *
 * @example
 * // Inside Modal.vue setup:
 * const { register, unregister, isTopmost, zIndex } = useModalStack(useId());
 */
import { computed, reactive } from "vue";

interface StackEntry {
  closeFn: () => void;
  id: string;
}

/** Base z-index for the first modal layer */
const BASE_Z_INDEX = 100;

/** Z-index increment per stacked modal */
const Z_INDEX_STEP = 10;

// ── Module-scoped singleton state ──────────────────────────────────────

const stack: StackEntry[] = reactive([]);
let scrollLockCount = 0;
let savedBodyOverflow = "";
let savedHtmlOverflow = "";

// ── Global Escape listener ─────────────────────────────────────────────

function handleEscape(event: KeyboardEvent) {
  // A control inside the modal (an open Select, Combobox or Dropdown) that
  // used this Escape to close itself marks it handled; the modal stays open.
  if (event.key !== "Escape" || event.defaultPrevented || stack.length === 0) return;

  event.stopPropagation();
  const topmost = stack[stack.length - 1];
  topmost.closeFn();
}

function installGlobalListener() {
  document.addEventListener("keydown", handleEscape);
}

function removeGlobalListener() {
  document.removeEventListener("keydown", handleEscape);
}

// ── Scroll lock (reference-counted) ────────────────────────────────────

function lockScroll() {
  if (scrollLockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    savedHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  }
  scrollLockCount++;
}

function unlockScroll() {
  if (scrollLockCount <= 0) return;
  scrollLockCount--;
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedBodyOverflow;
    document.documentElement.style.overflow = savedHtmlOverflow;
  }
}

// ── Public composable ──────────────────────────────────────────────────

export function useModalStack(id: string) {
  function register(closeFn: () => void) {
    stack.push({ closeFn, id });
    lockScroll();

    if (stack.length === 1) {
      installGlobalListener();
    }
  }

  function unregister() {
    const index = stack.findIndex((entry) => entry.id === id);
    // Bail out if this modal was never registered (opened). Modal.vue's
    // immediate watcher calls unregister() on mount when open=false,
    // and modals nested inside other modal content would otherwise
    // decrement the scroll lock count and break the parent's lock.
    if (index === -1) return;

    stack.splice(index, 1);
    unlockScroll();

    if (stack.length === 0) {
      removeGlobalListener();
    }
  }

  const isTopmost = computed(() => {
    return stack.length > 0 && stack[stack.length - 1].id === id;
  });

  const zIndex = computed(() => {
    const depth = stack.findIndex((entry) => entry.id === id);
    if (depth === -1) return BASE_Z_INDEX;
    return BASE_Z_INDEX + depth * Z_INDEX_STEP;
  });

  return { isTopmost, register, unregister, zIndex };
}

/**
 * Resets all module-scoped state. Only for use in tests.
 */
export function __resetModalStack() {
  stack.splice(0, stack.length);
  scrollLockCount = 0;
  savedBodyOverflow = "";
  savedHtmlOverflow = "";
  removeGlobalListener();
}
